/**
 * 动态部分检测器
 * 用于检测页面上的可用部分，供图标文本链接组件使用
 */
class DynamicSectionSelector {
  constructor() {
    this.sections = new Map();
    this.init();
  }

  /**
   * 初始化检测器
   */
  init() {
    // // console.log("DynamicSectionSelector: 初始化");
    this.detectSections();

    // 监听页面变化，重新检测部分
    this.observePageChanges();
  }

  /**
   * 检测页面上的可用部分
   */
  detectSections() {
    this.sections.clear();

    // 检测各种类型的部分
    this.detectSectionBlocks();
    this.detectCustomSections();
    this.detectProductSections();
    this.detectCommonSections();

    // // console.log("DynamicSectionSelector: 检测到", this.sections.size, "个部分");
  }

  /**
   * 检测 Shopify section 块
   */
  detectSectionBlocks() {
    // 检测所有 section 元素
    const sectionElements = document.querySelectorAll(
      'section[id], section[class*="section"], div[class*="section"]'
    );

    sectionElements.forEach((section, index) => {
      const id = section.id || `section-${index}`;
      let name = this.extractSectionName(section);

      if (name) {
        this.sections.set(id, name);
      }
    });
  }

  /**
   * 检测自定义部分
   */
  detectCustomSections() {
    // 检测带有特定 data 属性的元素
    const customElements = document.querySelectorAll(
      "[data-section], [data-block], [data-component]"
    );

    customElements.forEach((element) => {
      const id =
        element.dataset.section ||
        element.dataset.block ||
        element.dataset.component;
      let name = this.extractElementName(element);

      if (name && id) {
        this.sections.set(id, name);
      }
    });
  }

  /**
   * 检测产品相关部分
   */
  detectProductSections() {
    // 检测产品页面的特定部分
    const productElements = document.querySelectorAll(
      ".product-info, .product-description, .product-reviews, .product-gallery, .product-variants, .product-shipping, .product-warranty"
    );

    productElements.forEach((element) => {
      const id = element.id || element.className.split(" ")[0];
      let name = this.extractElementName(element);

      if (name && id) {
        this.sections.set(id, name);
      }
    });
  }

  /**
   * 检测通用部分
   */
  detectCommonSections() {
    // 检测常见的页面部分
    const commonSelectors = [
      ".header",
      ".footer",
      ".main-content",
      ".sidebar",
      ".navigation",
      ".breadcrumbs",
      ".pagination",
      ".search-results",
      ".filter",
      ".sort",
      ".gallery",
      ".slider",
      ".carousel",
      ".tabs",
      ".accordion",
      ".modal",
      ".form",
      ".button-group",
      ".icon-group",
    ];

    commonSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((element, index) => {
        const id = element.id || `${selector.replace(".", "")}-${index}`;
        let name = this.extractElementName(element);

        if (name && id) {
          this.sections.set(id, name);
        }
      });
    });
  }

  /**
   * 提取部分名称
   */
  extractSectionName(section) {
    // 尝试从各种属性中提取名称
    let name = "";

    // 从 aria-label 获取
    if (section.getAttribute("aria-label")) {
      name = section.getAttribute("aria-label");
    }
    // 从 title 获取
    else if (section.getAttribute("title")) {
      name = section.getAttribute("title");
    }
    // 从 class 名称推断
    else if (section.className) {
      const classes = section.className.split(" ");
      const meaningfulClasses = classes.filter(
        (cls) =>
          cls.includes("section") ||
          cls.includes("block") ||
          cls.includes("component") ||
          cls.includes("header") ||
          cls.includes("footer") ||
          cls.includes("main") ||
          cls.includes("sidebar")
      );

      if (meaningfulClasses.length > 0) {
        name = this.formatClassName(meaningfulClasses[0]);
      }
    }

    // 如果没有找到名称，尝试从内容推断
    if (!name) {
      const heading = section.querySelector("h1, h2, h3, h4, h5, h6");
      if (heading && heading.textContent.trim()) {
        name = heading.textContent.trim();
      }
    }

    return name || "未命名部分";
  }

  /**
   * 提取元素名称
   */
  extractElementName(element) {
    let name = "";

    // 从 aria-label 获取
    if (element.getAttribute("aria-label")) {
      name = element.getAttribute("aria-label");
    }
    // 从 title 获取
    else if (element.getAttribute("title")) {
      name = element.getAttribute("title");
    }
    // 从 class 名称推断
    else if (element.className) {
      const classes = element.className.split(" ");
      const meaningfulClasses = classes.filter(
        (cls) =>
          !cls.includes("js-") &&
          !cls.includes("is-") &&
          !cls.includes("has-") &&
          cls.length > 3
      );

      if (meaningfulClasses.length > 0) {
        name = this.formatClassName(meaningfulClasses[0]);
      }
    }

    // 如果没有找到名称，尝试从内容推断
    if (!name) {
      const textContent = element.textContent.trim();
      if (textContent && textContent.length < 50) {
        name = textContent;
      }
    }

    return name || "未命名元素";
  }

  /**
   * 格式化类名
   */
  formatClassName(className) {
    // 移除前缀
    let name = className
      .replace(/^section-/, "")
      .replace(/^block-/, "")
      .replace(/^component-/, "")
      .replace(/^js-/, "")
      .replace(/^is-/, "")
      .replace(/^has-/, "");

    // 转换为可读格式
    name = name
      .replace(/-/g, " ")
      .replace(/_/g, " ")
      .replace(/([A-Z])/g, " $1")
      .toLowerCase();

    // 首字母大写
    name = name.charAt(0).toUpperCase() + name.slice(1);

    return name;
  }

  /**
   * 监听页面变化
   */
  observePageChanges() {
    // 使用 MutationObserver 监听 DOM 变化
    if (window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        let shouldRedetect = false;

        mutations.forEach((mutation) => {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            // 检查是否有新的 section 元素
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                if (
                  node.tagName === "SECTION" ||
                  (node.className &&
                    typeof node.className === "string" &&
                    node.className.includes("section")) ||
                  (node.className &&
                    typeof node.className === "object" &&
                    node.className.toString().includes("section")) ||
                  (node.querySelector &&
                    node.querySelector('section, [class*="section"]'))
                ) {
                  shouldRedetect = true;
                }
              }
            });
          }
        });

        if (shouldRedetect) {
          // // console.log("DynamicSectionSelector: 检测到页面变化，重新检测部分");
          setTimeout(() => this.detectSections(), 100);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * 获取所有检测到的部分
   */
  getSections() {
    return this.sections;
  }

  /**
   * 获取部分名称
   */
  getSectionName(id) {
    return this.sections.get(id);
  }

  /**
   * 检查部分是否存在
   */
  hasSection(id) {
    return this.sections.has(id);
  }

  /**
   * 刷新检测
   */
  refresh() {
    this.detectSections();
  }
}

// 创建全局实例
window.dynamicSectionSelector = new DynamicSectionSelector();

// 导出类（如果使用模块系统）
if (typeof module !== "undefined" && module.exports) {
  module.exports = DynamicSectionSelector;
}
